import {
  GetQuestionCreationTaskByIdDocument,
  useSaveInDevelopmentQuestionMutation,
} from '@exam-management/graphql';
import { useQuestionCreationTaskDetails } from '@exam-management/question-creation-task-details';
import { parseContentForApi } from '@exam-management/rich-text-editor';
import { useNavigate } from '@exam-management/router';
import { Button, Flex, FormButton } from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import { useItemSetQuestionDetails, useSingleQuestionDetails } from '../../providers';
import { type SingleQuestionCreationEditFormInput } from '../single-question-creation-edit-form/single-question-creation.schema';

import { parseQuestionFormDataForApi } from './utils';

export function ItemSetSubQuestionEditFormActions() {
  const { showSnackbar } = useSnackbar();

  const { task } = useQuestionCreationTaskDetails();
  const formContext = useFormContext<SingleQuestionCreationEditFormInput>();
  const { singleQuestion: question } = useSingleQuestionDetails();
  const { itemSet } = useItemSetQuestionDetails();
  const navigate = useNavigate();
  const { formState } = formContext;
  const { isSubmitSuccessful } = formState;
  const itemSetCreation = task?.metadata?.requirement?.itemSetCreation?.find(
    (creation) => creation.questionGroupId === itemSet.id,
  );

  const isLoading = false;
  const [saveInDevelopmentQuestion] = useSaveInDevelopmentQuestionMutation({
    onError: () => {
      showSnackbar({
        message: 'Unable to Save Question',
        variant: 'error',
      });
    },
  });

  const saveItemSetSubQuestionQuestion = async (data: SingleQuestionCreationEditFormInput) => {
    const qBankStructureLevelItemId = data.location.SUB_TOPIC?.value as string;
    const payload = parseQuestionFormDataForApi(data, parseContentForApi, itemSet.id);

    const result = await saveInDevelopmentQuestion({
      variables: {
        input: {
          id: question.id,
          data: payload,
          qBankStructureLevelItemId,
        },
        taskId: task.id,
      },
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: GetQuestionCreationTaskByIdDocument,
          variables: {
            getTaskByIdInput: {
              taskId: task.id,
            },
          },
        },
      ],
      onCompleted: () => {
        showSnackbar({
          message: 'Question has been saved',
          variant: 'success',
        });
        navigate('/my-task/question-creation/:taskId/item-set/:questionId/create', {
          params: {
            taskId: task.id,
            questionId: itemSet.id,
          },
        });
      },
    });

    if (result.errors) throw result.errors;
  };

  const handleShowRequiredFieldsError = () => {
    showSnackbar({
      message: 'Please complete all required fields',
      variant: 'error',
    });
  };

  const handleSaveAndCreateNew = async () => {
    await formContext.handleSubmit(async (data) => {
      await saveItemSetSubQuestionQuestion(data);
    }, handleShowRequiredFieldsError)();
  };

  useEffect(() => {
    if (isSubmitSuccessful) {
      formContext.reset(formContext.getValues());
    }
  }, [isSubmitSuccessful, formContext, navigate, task.id, itemSet.id]);

  const shouldAllowSaveAndCreateNew = itemSetCreation
    ? itemSetCreation?.questionCreationCount < itemSetCreation?.size
    : false;

  return (
    <Flex sx={{ gap: 4 }}>
      <FormButton
        type='submit'
        color='primary'
        variant='contained'
        disabled={formContext.formState.isSubmitting || !formContext.formState.isDirty}
        loading={formContext.formState.isSubmitting}
        label='Save'
      />
      {shouldAllowSaveAndCreateNew ? (
        <Button
          type='button'
          color='secondary'
          variant='outlined'
          disabled={formContext.formState.isSubmitting || !formContext.formState.isDirty}
          loading={isLoading}
          onClick={handleSaveAndCreateNew}
        >
          Save & Create New
        </Button>
      ) : null}
    </Flex>
  );
}
